import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from './useAdminApi';
import type { AdminCustomerLocation } from './useAdminCustomerDetail';

/**
 * Hooks for the customer-detail Locations tab (admin-customer-locations).
 *
 * The local location list itself rides on useAdminCustomerDetail (PostgREST
 * read) — these hooks cover the Service Fusion panel and the mutations, and
 * invalidate the detail query so every surface refreshes together.
 */

export interface SfLocationRow {
  id?: number | string;
  nickname?: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state_prov?: string;
  postal_code?: string;
  is_primary?: boolean;
  is_bill_to?: boolean;
  is_gated?: boolean;
  gate_instructions?: string;
  /** Server-computed: an equivalent local row already exists. */
  on_file: boolean;
}

interface SfLocationsResponse {
  ok: true;
  sf: {
    checked: boolean;
    sf_customer_id?: number | null;
    error?: string | null;
    locations?: SfLocationRow[];
  };
}

/** Editable location fields (create + update payload shape). */
export interface LocationFields {
  display_name?: string;
  location_code?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  delivery_notes?: string;
  /** Direct line at this delivery location (the store's own phone). */
  phone?: string;
}

/** Live Service Fusion locations for this customer. Fetch is on-demand
 * (enabled flag) — it costs an SF round-trip, so the panel asks for it. */
export function useSfLocations(customerId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'sf-locations', customerId],
    enabled,
    staleTime: 60 * 1000,
    retry: false,
    queryFn: async () => {
      const res = await adminFetch<SfLocationsResponse>(
        `admin-customer-locations?customer_id=${encodeURIComponent(customerId)}&sf=1`,
      );
      return res.sf;
    },
  });
}

function useLocationMutation<TVars>(
  customerId: string,
  toBody: (vars: TVars) => Record<string, unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: TVars) => {
      return adminFetch<{ ok: true; locations: AdminCustomerLocation[] }>(
        'admin-customer-locations',
        {
          method: 'POST',
          body: JSON.stringify({ customer_id: customerId, ...toBody(vars) }),
        },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customer-detail', customerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'sf-locations', customerId] });
    },
  });
}

export function useCreateLocation(customerId: string) {
  return useLocationMutation<LocationFields>(customerId, (location) => ({
    action: 'create',
    location,
  }));
}

export function useUpdateLocation(customerId: string) {
  return useLocationMutation<{ locationId: string; fields: LocationFields }>(
    customerId,
    ({ locationId, fields }) => ({ action: 'update', location_id: locationId, location: fields }),
  );
}

export function useSetLocationActive(customerId: string) {
  return useLocationMutation<{ locationId: string; value: boolean }>(
    customerId,
    ({ locationId, value }) => ({ action: 'set_active', location_id: locationId, value }),
  );
}

/**
 * Mark a location as the account bill-to.
 *
 * The billing address is a LOCATION (Sky, 2026-08-31) — it is resolved here,
 * on the tab where Service Fusion's and QuickBooks' addresses are already
 * listed, rather than being typed a second time in billing settings. The
 * server clears the previous bill-to, sets this one, and pushes it out to SF
 * and QBO.
 */
export function useSetBillingLocation(customerId: string) {
  return useLocationMutation<{ locationId: string }>(customerId, ({ locationId }) => ({
    action: 'set_billing',
    location_id: locationId,
  }));
}

export function useImportSfLocation(customerId: string) {
  return useLocationMutation<SfLocationRow>(customerId, (sfLocation) => ({
    action: 'import_sf',
    sf_location: sfLocation,
  }));
}

/* ------------------------------------------------------------------ */
/* SF customer link — the fix for "Customer Name can not be found"     */
/* (submit-order 422 on unlinked customers whose QBO name isn't an     */
/* exact SF customer name).                                            */
/* ------------------------------------------------------------------ */

export interface SfCustomerCandidate {
  id: number;
  customer_name: string | null;
  qbo_id: number | string | null;
  /** SF record whose qbo_id matches this customer — the near-certain pick. */
  qbo_match: boolean;
  /** Already the linked SF customer. */
  linked: boolean;
}

/** Live SF customer search for the manual link flow. Debounce in the UI —
 * every call is an SF round-trip. */
export function useSfCustomerSearch(customerId: string, term: string) {
  const trimmed = term.trim();
  return useQuery({
    queryKey: ['admin', 'sf-customer-search', customerId, trimmed],
    enabled: trimmed.length >= 2,
    staleTime: 60 * 1000,
    retry: false,
    queryFn: async () => {
      const res = await adminFetch<{ ok: true; candidates: SfCustomerCandidate[] }>(
        `admin-customer-locations?customer_id=${encodeURIComponent(customerId)}` +
          `&sf_search=${encodeURIComponent(trimmed)}`,
      );
      return res.candidates;
    },
  });
}

/** Stamp (or clear, with null) customers.sf_customer_id. */
export function useLinkSfCustomer(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sfCustomerId: number | null) => {
      return adminFetch<{ ok: true; sf_customer_id: number | null; sf_customer_name: string | null }>(
        'admin-customer-locations',
        {
          method: 'POST',
          body: JSON.stringify({
            customer_id: customerId,
            action: 'link_sf',
            sf_customer_id: sfCustomerId,
          }),
        },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'customer-detail', customerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'sf-locations', customerId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'sf-customer-search', customerId] });
    },
  });
}
